import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink } from "@trpc/client";
import { useMemo, useState } from "react";
import { supabase } from "~/utils/supabase.client";
import { trpc } from "~/utils/trpc";

function getBackendUrl() {
  if (typeof window !== "undefined") {
    return (window as any).__ENV__?.BACKEND_URL ?? "http://localhost:2023";
  }
  return process.env.VITE_BACKEND_URL ?? "http://localhost:2023";
}

export function TRPCProvider({
  children,
  serverToken,
}: {
  children: React.ReactNode;
  serverToken?: string;
}) {
  const backendUrl = useMemo(() => getBackendUrl(), []);
  const [queryClient] = useState(() => new QueryClient());
  const [trpcClient] = useState(() =>
    trpc.createClient({
      links: [
        httpBatchLink({
          url: backendUrl,
          headers: async () => {
            if (typeof window === "undefined") {
              return serverToken ? { Authorization: `Bearer ${serverToken}` } : {};
            }

            const {
              data: { session },
            } = await supabase.auth.getSession();
            return session?.access_token
              ? { Authorization: `Bearer ${session.access_token}` }
              : {};
          },
          // Retry once on 401: explicitly refresh the session and resend with new token
          fetch: async (url, options) => {
            const response = await fetch(url, options);

            if (response.status === 401 && typeof window !== "undefined") {
              const { data } = await supabase.auth.refreshSession();
              if (data.session) {
                const retryHeaders = new Headers(options?.headers);
                retryHeaders.set(
                  "Authorization",
                  `Bearer ${data.session.access_token}`,
                );
                return fetch(url, { ...options, headers: retryHeaders });
              }
              // Refresh failed — force login
              window.location.href = "/login";
            }

            return response;
          },
        }),
      ],
    }),
  );

  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </trpc.Provider>
  );
}
